// Navigator abstraction that lets the FE share / copy / locate /
// navigate / OAuth against the host page when running inside the embed
// iframe, falling back to the local window when standalone.
//
// Mirrors and upgrades the v2 reference design (context/team-lead/
// v2-reference/app-navigator.ts):
//
//   - Adds a `v: <PROTOCOL_VERSION>` envelope field so the SDK and FE
//     can roll forward independently.
//   - Pins postMessage `targetOrigin` to the SDK's host origin (when
//     known) instead of "*", reducing the surface for forged replies
//     from unrelated iframes / windows on the host page.
//   - Verifies inbound `:res` messages came from `window.parent` and
//     match the request `requestId`.
//   - Adds an `ff-oauth` event for relayed OAuth popups (Safari + iframe
//     fix; the SDK opens the popup from the host's window context so
//     `window.opener` survives COOP, then forwards the auth code down
//     to the iframe).
//
// Communication uses a request/response postMessage protocol with
// `ff-<event>:ask` from the iframe and `ff-<event>:res` from the host.
// Each call has a request id so concurrent calls don't race, and a
// timeout so a host that doesn't yet implement an event fails fast and
// lets the FE fall back to local behaviour where one exists.
//
// HOST-SIDE: the SDK script on the host page listens for `:ask` events
// and replies with `:res` events. See apps/sdk/src/services/embed/.

import { useAppStore } from "@/stores/app.store";
import { getSdkOrigin } from "@/composables/auth/use-embed-authentication-broker";
import { buildHostEmbeddedAppUrl } from "@f3/shared/embed-url";

export interface HostLocation {
  href: string;
  origin: string;
  pathname: string;
  queryString: string;
  hash: string;
}

export interface SharePayload {
  title: string | undefined;
  text: string | undefined;
  url: string;
}

export interface OAuthResult {
  code: string;
  state: string;
  redirect_uri: string;
}

const PROTOCOL_VERSION = 1;

const ASK_TIMEOUT_MS = 1500;
// OAuth dance involves a human typing credentials; allow generous time.
const OAUTH_ASK_TIMEOUT_MS = 5 * 60 * 1000;

const events = {
  location: { ask: "ff-location:ask", res: "ff-location:res" },
  share: { ask: "ff-share:ask", res: "ff-share:res" },
  clipboard: { ask: "ff-clipboard:ask", res: "ff-clipboard:res" },
  navigate: { ask: "ff-navigate:ask", res: "ff-navigate:res" },
  oauth: { ask: "ff-oauth:ask", res: "ff-oauth:res" },
} as const;

function isEmbed(): boolean {
  // useAppStore is reactive; we only need the synchronous boolean. Re-reads
  // each call to avoid caching a stale value if the SDK toggles modes.
  try {
    return useAppStore().isEmbed;
  } catch {
    // Pinia not initialized (e.g. running before app boot) — fall through
    // to local-only behaviour.
    return false;
  }
}

function makeRequestId(): string {
  // Math.random is sufficient — we only need uniqueness within a single
  // page session against an honest host SDK. Crypto would be overkill.
  return `ff-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

interface AskResponse {
  ok: boolean;
  error?: string;
  [key: string]: unknown;
}

function ask<T extends AskResponse>(
  event: { ask: string; res: string },
  data: Record<string, unknown>,
  timeoutMs: number = ASK_TIMEOUT_MS,
): Promise<T> {
  const requestId = makeRequestId();
  const targetOrigin = getSdkOrigin() ?? "*";

  return new Promise<T>((resolve, reject) => {
    const handler = (e: MessageEvent) => {
      // Only accept replies from our parent window (drops cross-window
      // forgery attempts) and only when origin pinning is established.
      if (e.source && e.source !== window.parent) return;
      const sdkOrigin = getSdkOrigin();
      if (sdkOrigin && e.origin !== sdkOrigin) return;

      const payload = e.data as {
        key?: string;
        v?: number;
        requestId?: string;
        data?: T;
      } | null;
      if (!payload || payload.key !== event.res) return;
      if (payload.requestId && payload.requestId !== requestId) return;
      if (payload.v !== undefined && payload.v !== PROTOCOL_VERSION) {
        // Unknown protocol version — log and fall through. The
        // best-effort path will still produce a usable result if the
        // shape is compatible.
        console.warn("[app-navigator] unknown protocol version on res", {
          v: payload.v,
        });
      }

      window.removeEventListener("message", handler);
      clearTimeout(timeoutId);
      const data = payload.data as T | undefined;
      if (data?.ok === false) {
        reject(new Error(data.error ?? "host rejected ask"));
        return;
      }
      resolve(data ?? ({ ok: true } as T));
    };

    const timeoutId = setTimeout(() => {
      window.removeEventListener("message", handler);
      reject(new Error(`[app-navigator] ${event.ask} timed out`));
    }, timeoutMs);

    window.addEventListener("message", handler);
    // Best effort — the host script may not exist yet. The timeout above
    // unblocks us in that case.
    try {
      window.parent.postMessage(
        { key: event.ask, v: PROTOCOL_VERSION, requestId, data },
        targetOrigin,
      );
    } catch (err) {
      window.removeEventListener("message", handler);
      clearTimeout(timeoutId);
      reject(err);
    }
  });
}

function localLocation(): HostLocation {
  const loc = window.document.location;
  const queryString = loc.search.startsWith("?")
    ? loc.search.slice(1)
    : loc.search;
  return {
    href: loc.href,
    origin: loc.origin,
    pathname: loc.pathname,
    queryString,
    hash: loc.hash,
  };
}

async function askHostLocation(): Promise<HostLocation> {
  const data = await ask<AskResponse & HostLocation>(events.location, {});
  return {
    href: data.href,
    origin: data.origin,
    pathname: data.pathname,
    queryString: data.queryString,
    hash: data.hash ?? "",
  };
}

function resolveLocalAppUrl(appRouteHref: string): URL {
  const url = new URL(appRouteHref, window.location.origin);
  if (url.origin !== window.location.origin) {
    throw new Error("[app-navigator] Shareable app URLs must stay on FanFest");
  }
  return url;
}

/**
 * Returns the location of the *host* page (when embedded) so generated
 * share / copy links point at the host URL, not the iframe's URL. Falls
 * back to the iframe's own location if the host SDK isn't responding —
 * this preserves the legacy behaviour rather than throwing.
 */
export async function getHostLocation(): Promise<HostLocation> {
  if (!isEmbed()) return localLocation();
  try {
    return await askHostLocation();
  } catch (err) {
    console.warn(
      "[app-navigator] getHostLocation fell back to iframe location:",
      err,
    );
    return localLocation();
  }
}

/**
 * Builds a public URL for a FanFest route. Standalone, this is the
 * normal FanFest app URL. Embedded, this preserves the host page's
 * current path/query/hash and stores the FanFest route in the shared
 * `ff_r` query param so the SDK can boot the iframe directly into that
 * route. When `channelId` is passed, the channel prefix is stripped
 * from the route (the SDK re-prepends it on the read side).
 */
export async function buildShareableAppUrl(
  appRouteHref: string,
  options: { channelId?: string } = {},
): Promise<string> {
  const appUrl = resolveLocalAppUrl(appRouteHref);
  const appRoute = `${appUrl.pathname}${appUrl.search}${appUrl.hash}`;

  if (!isEmbed()) {
    return appUrl.href;
  }

  try {
    const hostLocation = await askHostLocation();
    return buildHostEmbeddedAppUrl(hostLocation.href, appRoute, {
      channelId: options.channelId,
    });
  } catch (err) {
    console.warn(
      "[app-navigator] buildShareableAppUrl fell back to iframe URL:",
      err,
    );
    return appUrl.href;
  }
}

/**
 * Returns the base URL to persist against a generated referral code. In
 * embed mode this is the exact host URL so `/s/:channel/:code` redirects
 * back to the host with the referral route encoded by the API.
 */
export async function getShareableLinkBaseUrl(): Promise<string> {
  if (!isEmbed()) return window.location.origin;

  try {
    return (await askHostLocation()).href;
  } catch (err) {
    console.warn(
      "[app-navigator] getShareableLinkBaseUrl fell back to iframe origin:",
      err,
    );
    return window.location.origin;
  }
}

/**
 * Triggers a native share dialog. In embed context the host SDK opens its
 * own share UI on the host page; standalone, falls back to navigator.share.
 * Throws on AbortError so callers can distinguish user-cancelled vs failed.
 */
export async function share(payload: SharePayload): Promise<void> {
  if (isEmbed()) {
    try {
      await ask(events.share, { ...payload });
      return;
    } catch (err) {
      console.warn("[app-navigator] share fell back to navigator.share:", err);
    }
  }
  if (
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function"
  ) {
    await navigator.share(payload);
    return;
  }
  throw new Error("[app-navigator] No share path available");
}

/**
 * Writes text (or a ClipboardItem) to the clipboard. In embed context the
 * host SDK runs the clipboard call on the host page (avoids permission
 * scoping issues across iframes); standalone, uses navigator.clipboard.
 */
export async function writeToClipboard(
  value: string | ClipboardItem,
): Promise<void> {
  if (isEmbed() && typeof value === "string") {
    // ClipboardItem can't be cloned across postMessage boundaries reliably;
    // limit the host-relayed path to plain strings. Callers needing rich
    // clipboard content can always run within the iframe via navigator.
    try {
      await ask(events.clipboard, { text: value });
      return;
    } catch (err) {
      console.warn(
        "[app-navigator] writeToClipboard fell back to navigator:",
        err,
      );
    }
  }
  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard?.write &&
    value instanceof ClipboardItem
  ) {
    await navigator.clipboard.write([value]);
    return;
  }
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(typeof value === "string" ? value : "");
    return;
  }
  throw new Error("[app-navigator] No clipboard path available");
}

/**
 * Performs a top-level navigation. In embed context the host SDK navigates
 * the host page; standalone, sets window.location. Use for absolute URLs
 * meant to leave the iframe (e.g. share-link clicks).
 */
export async function navigateTo(url: string): Promise<void> {
  if (isEmbed()) {
    try {
      await ask(events.navigate, { url });
      return;
    } catch (err) {
      console.warn(
        "[app-navigator] navigateTo fell back to window.location:",
        err,
      );
    }
  }
  window.location.assign(url);
}

/**
 * Embed-only: have the host SDK open the OAuth provider's login page in
 * a popup, listen for the `oauth-callback` message that lands on the
 * host (because window.opener is the host, not the iframe), and forward
 * the auth code back down to us.
 *
 * Why not run the popup ourselves? When `window.open` is called from
 * inside the iframe, modern browsers (especially Safari with ITP) treat
 * the popup as a third-party with no shared storage and severed
 * `window.opener`. The popup's auth-success postMessage never lands.
 * Routing through the host fixes both halves: the popup is a sibling of
 * the host, and the SDK forwards the code back to us via `ff-oauth:res`.
 *
 * Throws on cancel / timeout / popup blocked / SDK not present so the
 * caller can decide whether to fall back to an in-iframe popup.
 */
export async function startOAuth(authUrl: string): Promise<OAuthResult> {
  if (!isEmbed()) {
    throw new Error("[app-navigator] startOAuth only valid in embed context");
  }
  const data = await ask<AskResponse & OAuthResult>(
    events.oauth,
    { authUrl },
    OAUTH_ASK_TIMEOUT_MS,
  );
  return {
    code: data.code,
    state: data.state,
    redirect_uri: data.redirect_uri,
  };
}

// Internal — exported only for tests so they can drive the protocol
// without spinning up Pinia.
export const __INTERNAL__ = {
  events,
  ASK_TIMEOUT_MS,
  OAUTH_ASK_TIMEOUT_MS,
  PROTOCOL_VERSION,
  makeRequestId,
};
